import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { UserForm } from "./UserForm";
import { axiosNewInstance } from "../../config/Api";
import toast from "react-hot-toast";
import "./user.scss";
import LangData from "../common/LangData";

export default function User() {
  const [showForm, setShowForm] = useState(false);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [editUser, setEditUser] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ name: "", status: "" });

  const token = localStorage.getItem("authToken");
  const history = useHistory();
  const topRef = useRef(null);

  // ✅ Get user from Redux
  const user = useSelector((state) => state?.user);

  // ✅ Check Add User Permission
  const hasAddUserPermission = Number(user?.loginuser?.role_id) === 1;

  // ✅ Check Edit Permission
  const hasEditPermission = (userData) =>
    user?.loginData?.some((p) => p.permission_id === 3) ||
    user?.loginuser?.id === 1;

  // Fetch Users
  const fetchUsers = async (page = 1) => {
    const FETCH_USER_URL = "/api-app/get-users";
    setLoading(true);

    try {
      const response = await axiosNewInstance.post(
        FETCH_USER_URL,
        {
          page,
          name: filters.name || undefined,
          status: filters.status ? Number(filters.status) : undefined,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const userData = response.data?.data?.users || {};
      setUsers(userData?.data || []);
      setCurrentPage(userData?.current_page || 1);
      setTotalPages(userData?.last_page || 1);
    } catch (error) {
      if (
        error.response?.data?.status === 0 &&
        error?.response?.data?.message === "User not authenticated"
      ) {
        localStorage.removeItem("authToken");
        history.push("/login");
      } else {
        console.error("Error fetching users:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch Roles
  const fetchRoles = async () => {
    try {
      if (!token) throw new Error("No auth token found");

      const response = await axiosNewInstance.post(
        "/api-app/get-roles",
        { is_paginate: "0" },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response?.data?.status === 1 && response?.data?.data?.roles) {
        // const rolesData = Object.values(response.data?.data?.roles)?.map(
        //   (role) => ({
        //     id: role.id,
        //     name: role.name,
        //   })
        // );
        setRoles(response.data?.data?.roles);
      }
    } catch (error) {
      if (
        error.response?.data?.status === 0 &&
        error?.response?.data?.message === "User not authenticated"
      ) {
        localStorage.removeItem("authToken");
        history.push("/login");
      } else {
        console.error("Error fetching roles:", error);
      }
    } finally {
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, []);

  // Re-fetch users when filters change

  const handleCsvUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("csv_file", file);

    setLoading(true);
    try {
      const response = await axiosNewInstance.post("/api-app/upload-users-csv", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.status === 1) {
        toast.success(response.data.message);
        setUploadResults(response.data);
        setShowResults(true);
        fetchUsers();
      } else {
        toast.error(response.data.message || "Upload failed");
      }
    } catch (error) {
      console.error("Error uploading CSV:", error);
      toast.error(error.response?.data?.message || "Error uploading CSV");
    } finally {
      setLoading(false);
      // Reset file input
      event.target.value = "";
    }
  };

  const handleDownloadCsv = async () => {
    setLoading(true);
    try {
      const response = await axiosNewInstance.post(
        "/api-app/export-users-csv",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `users_export_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success("CSV Downloaded successfully");
    } catch (error) {
      console.error("Error downloading CSV:", error);
      toast.error("Error downloading CSV");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(1);
  }, [filters]);

  // Handle Page Change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchUsers(newPage);
    }
  };

  // Handle Search and Filter Updates
  const handleSearchAndFilter = () => {
    fetchUsers(1);
  };

  const handleFormSubmit = () => {
    fetchUsers();
    setShowForm(false);
  };

  const handleFormCancel = () => {
    setShowForm(false);
  };

  return (
    <div className="container mt-4 userContainer__area">
      <div
        className={`d-flex align-items-center mb-4 bg-white rounded ${!showForm ? "justify-content-between" : "justify-content-end"
          } ${showForm ? "d-none" : ""}`}
        style={{ gap: "15px" }}
      >
        {!showForm && (
          <div className="d-flex gap-2 align-items-center">
            <div>
              <label htmlFor="name-filter" className="form-label m-0">
                User Name:
              </label>
              <input
                type="text"
                id="name-filter"
                className="form-control form-control-sm"
                placeholder="Search by name"
                style={{ width: "200px", height: "40px" }}
                value={filters.name}
                onChange={(e) =>
                  setFilters({ ...filters, name: e.target.value })
                }
              />
            </div>
            <div>
              <label htmlFor="status-filter" className="form-label m-0">
                Status:
              </label>
              <select
                id="status-filter"
                className="form-control form-control-sm"
                style={{ width: "150px", height: "40px" }}
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value })
                }
              >
                <option value="">All</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
            <button
              className="btn btn-sm btn-primary mt-4"
              style={{ height: "40px", backgroundColor: "#004E8F" }}
              onClick={handleSearchAndFilter}
            >
              <LangData keyname="placeholder_search_box" />
            </button>
          </div>
        )}

        {/* ✅ Show "Add User" button only if user has permission_id 2 */}
        {hasAddUserPermission && !showForm && (
          <div className="d-flex justify-content-end mt-4 gap-2">
            <input
              type="file"
              accept=".csv"
              id="csv-upload-input"
              className="d-none"
              onChange={handleCsvUpload}
            />
            <button
              type="button"
              className="btn btn-success"
              style={{ padding: "8px 25px" }}
              onClick={() => document.getElementById("csv-upload-input").click()}
            >
              Upload CSV
            </button>
            <button
              type="button"
              className="btn btn-info text-white"
              style={{ padding: "8px 25px" }}
              onClick={handleDownloadCsv}
            >
              Download CSV
            </button>
            <button
              type="button"
              className="btn btn-primary"
              style={{ backgroundColor: "#004E8F", padding: "8px 25px" }}
              onClick={() => {
                setShowForm(true);
                setEditUser({});
              }}
            >
              <LangData keyname="label_create_user" />
            </button>
          </div>
        )}
      </div>


      {/* --- CSV Upload Results Display --- */}
      {showResults && uploadResults && (
        <div className="alert alert-info mt-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">CSV Export Result</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShowResults(false)}
            ></button>
          </div>
          <p className="mb-2">
            <strong>Uploaded:</strong> {uploadResults.inserted_count} | <strong>Skipped:</strong> {uploadResults.skipped_count}
          </p>
          {uploadResults.skipped_rows?.length > 0 && (
            <div
              className="table-responsive"
              style={{ maxHeight: "300px", overflowY: "auto" }}
            >
              <table className="table table-sm table-bordered bg-white mt-2">
                <thead className="table-light">
                  <tr>
                    <th>Row</th>
                    <th>Name</th>
                    <th>Mobile</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadResults.skipped_rows.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.row}</td>
                      <td>{row.name || "N/A"}</td>
                      <td>{row.mobile || "N/A"}</td>
                      <td className="text-danger">{row.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div ref={topRef} className="mb-4">
          <UserForm
            editUser={editUser}
            onFormSubmit={handleFormSubmit}
            roles={roles}
            handleFormCancel={handleFormCancel}
          />
        </div>
      )}

      {!showForm && (
        <div className="card shadow-sm user__table_header">
          <div
            className="card-header text-white"
            style={{ background: "#004E8F" }}
          >
            <h5 className="mb-0"> <LangData keyname="label_user_list" />
            </h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover table-bordered mb-0">
                <thead>
                  <tr>
                    <th scope="col"><LangData keyname="label_serial" /></th>
                    <th scope="col">Employee ID</th>
                    <th scope="col"><LangData keyname="label_name" /></th>
                    <th scope="col"><LangData keyname="label_customer_id" /></th>
                    <th scope="col"><LangData keyname="label_mobile" /></th>
                    <th scope="col"><LangData keyname="label_user_type" /></th>
                    <th scope="col"><LangData keyname="label_status" /></th>
                    <th scope="col"><LangData keyname="label_action" /></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : users?.length > 0 ? (
                    users.map((user, index) => (
                      <tr key={user.id}>
                        <th>{index + 1 + (currentPage - 1) * 50}</th>
                        <td>{user.emp_id || "N/A"}</td>
                        <td>{user.en_first_name}</td>
                        <td>{user.login_id || "N/A"}</td>
                        <td>{user.mobile}</td>
                        <td>
                          {roles.find((r) => Number(r.id) === user.role_id)
                            ?.name || "N/A"}
                        </td>
                        <td>{user.status === 1 ? "Active" : "Inactive"}</td>
                        {/* ✅ Show "Edit" button only if user has permission_id 3 */}
                        <td>
                          {hasEditPermission(user) ? (
                            <button
                              onClick={() =>
                                setEditUser(user) || setShowForm(true)
                              }
                              className="btn btn-sm btn-outline-warning"
                            >
                              Edit
                            </button>
                          ) : (
                            <td className="text-center">
                              <button
                                disabled={true}
                                className="btn btn-sm btn-outline-warning"
                              >
                                N/P
                              </button>
                            </td>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center">
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!showForm && (
        <div className="d-flex justify-content-between mt-3 mb-3">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
